import Data from 'data';
import Store from 'store';
import { ProfileStore } from 'store';
import { itemFix, itemsEqual, itemUpdate } from "./itemReducers";
import { MaxProfileSetName } from 'common';
import { isSubType, isSubTypeEx, otherHandSlot, presetItems } from './itemTypes';
import { monsterFix } from './monster/picker';

export function stateFindItem(state, id) {
  const locations = [];
  for (const oid in state.items) {
    const item = state.items[oid];
    if (item?.socketedItems?.includes(id)) {
      locations.push({item, id: oid});
    }
  }
  for (const profile of state.profiles) {
    if (Object.values(profile.items).includes(id)) {
      locations.push({profile});
    }
    if (Object.values(profile.mercItems).includes(id)) {
      locations.push({profile, mercenary: true});
    }
    if (profile.inventory.includes(id)) {
      locations.push({profile, inventory: true});
    }
    if (profile.cube.includes(id)) {
      locations.push({profile, cube: true});
    }
  }
  return locations;
}

export function stateRemoveItem(state, id) {
  if (!state.items[id]) return state;
  state = {...state};
  state.items = {...state.items};
  delete state.items[id];
  for (const oid in state.items) {
    const item = state.items[oid];
    if (item?.socketedItems?.includes(id)) {
      state.items[oid] = itemUpdate({...item, socketedItems: item.socketedItems.filter(sid => sid !== id)}, state);
    }
  }
  const oldps = state.profiles;
  for (let i = 0; i < state.profiles.length; ++i) {
    const profile = state.profiles[i];
    let newp = null;
    if (Object.values(profile.items).includes(id)) {
      if (!newp) newp = {...profile};
      newp.items = {};
      for (const slot in profile.items) {
        if (profile.items[slot] !== id) {
          newp.items[slot] = profile.items[slot];
        }
      }
    }
    if (Object.values(profile.mercItems).includes(id)) {
      if (!newp) newp = {...profile};
      newp.mercItems = {};
      for (const slot in profile.mercItems) {
        if (profile.mercItems[slot] !== id) {
          newp.mercItems[slot] = profile.mercItems[slot];
        }
      }
    }
    if (profile.inventory.includes(id)) {
      if (!newp) newp = {...profile};
      newp.inventory = [];//profile.inventory.slice();
      profile.inventory.forEach((iid, index) => {
        if (iid !== id) newp.inventory[index] = iid;
      });
    }
    if (profile.cube.includes(id)) {
      if (!newp) newp = {...profile};
      newp.cube = [];//profile.cube.slice();
      profile.cube.forEach((iid, index) => {
        if (iid !== id) newp.cube[index] = iid;
      });
    }
    if (newp) {
      if (state.profiles === oldps) state.profiles = state.profiles.slice();
      state.profiles[i] = newp;
    }
  }
  return state;
}

export function profileFix(profile, items) {
  profile = {...ProfileStore.initialState, ...profile, id: Store.newId()};
  if (typeof profile.name !== "string") profile.name = `Set ${profile.id}`;
  else if (profile.name.length > MaxProfileSetName) profile.name = profile.name.substring(0, MaxProfileSetName);
  if (!Data.charStats[profile.class]) return null;
  const newItems = {};
  if (profile.items && typeof profile.items === "object") {
    for (const [slot, id] of Object.entries(profile.items)) {
      if (items[id] || presetItems[id]) newItems[slot] = id;
    }
  }
  profile.items = newItems;
  const newInventory = [];
  if (Array.isArray(profile.inventory)) {
    profile.inventory.forEach((id, index) => {
      if (items[id] || presetItems[id]) newInventory[index] = id;
    });
  }
  profile.inventory = newInventory;
  const newCube = [];
  if (Array.isArray(profile.cube)) {
    profile.cube.forEach((id, index) => {
      if (items[id] || presetItems[id]) newCube[index] = id;
    });
  }
  profile.cube = newCube;
  const mercItems = {};
  if (profile.mercItems && typeof profile.mercItems === "object") {
    for (const [slot, id] of Object.entries(profile.mercItems)) {
      if (items[id] || presetItems[id]) mercItems[slot] = id;
    }
  }
  profile.mercItems = mercItems;
  if (profile.ironGolem && !(items[profile.ironGolem] || presetItems[profile.ironGolem])) {
    profile.ironGolem = null;
  }
  if (!profile.active || typeof profile.active !== "object") {
    profile.active = {};
  }
  if (!Data.hireling[profile.merc]) {
    profile.merc = null;
  }
  if (profile.monster) {
    profile.monster = monsterFix(profile.monster, profile.difficulty, profile.level);
  }
  return profile;
}

export function stateFix(state, importId, userId) {
  const result = {...Store.initialState, ...state, id: Store.newId()};

  if (!result.profiles[result.activeProfile]) {
    result.activeProfile = 0;
  }

  if (!result.active || typeof result.active !== "object") {
    result.active = {};
  }

  result.items = {};
  if (state?.items) for (const id in state.items) {
    const item = itemFix(state.items[id]);
    if (item) result.items[id] = item;
  }

  result.profiles = result.profiles.map(profile => profileFix(profile, result.items)).filter(Boolean);

  if (!result.profiles.length) {
    result.profiles.push({...ProfileStore.initialState, id: Store.newId()});
  }

  if (result.buffs && typeof result.buffs !== 'object') {
    result.buffs = {};
  } else {
    result.buffs = {...result.buffs};
    for (const id in result.buffs) {
      if (!Array.isArray(result.buffs[id])) {
        delete result.buffs[id];
      }
    }
  }

  if (importId) {
    if (stateIsSingleProfile(result)) {
      result.profiles[0].imported = true;
      result.profiles[0].importedId = importId;
      result.profiles[0].user = userId;
    } else {
      result.imported = true;
      result.importedId = importId;
      result.user = userId;
    }
  }

  return result;
}

export function hirelingIsClass(mercClass, charClass) {
  if (Data.info.pd2) {
    switch (mercClass) {
    case 271: return charClass === 'ama';
    case 359: return charClass === 'pal' || charClass === 'sor';
    case 561: return charClass === 'bar';
    default: return false;
    }
  } else {
    switch (mercClass) {
    case 271: return charClass === 'ama';
    case 561: return charClass === 'bar';
    case 560: return charClass === 'bar';
    default: return false;
    }
  }
}

export function hirelingWeaponType(charClass, type) {
  if (Data.info.pd2) {
    switch (charClass) {
    case 271: return isSubTypeEx(type, 'bow', 'xbow');
    case 338: return isSubTypeEx(type, 'pole', 'spea');
    case 359: return isSubTypeEx(type, 'swor', 'shie', 'wand', 'scep', 'mace', 'knif', 'ashd', 'orb');
    case 561: return isSubTypeEx(type, 'swor', 'hamm', 'mace', 'axe');
    default: return false;
    }
  } else {
    switch (charClass) {
    case 271: return isSubType(type, 'bow');
    case 338: return isSubTypeEx(type, 'pole', 'spea');
    case 359: return isSubTypeEx(type, 'swor', 'shie');
    case 561: return isSubType(type, 'swor');
    case 560: return isSubTypeEx(type, 'swor');
    default: return false;
    }
  }
}

const canonicalSlot = {rarm2: 'rarm', larm2: 'larm'};
export function profileCanEquipType(profile, slot, typeId, merc) {
  const type = Data.itemTypes[typeId];
  slot = canonicalSlot[slot] || slot;
  if (!type) return false;
  if (type.bodyloc1 !== slot && type.bodyloc2 !== slot) return false;
  if (type.class && (merc || type.class !== profile.class) &&
    !(merc && hirelingIsClass(Data.hireling[profile.merc]?.[0]?.class, type.class))) return false;
  if (merc && (slot === 'rarm' || slot === 'larm')) {
    const info = Data.hireling[profile.merc]?.[0];
    if (!info) return false;
    return hirelingWeaponType(info.class, typeId);
  }
  return true;
}
export function profileCanEquip(profile, slot, item, merc) {
  const base = Data.items[item.base];
  if (!profileCanEquipType(profile, slot, base?.type, merc)) return false;
  if (merc && (slot === 'rarm' || slot === 'larm')) {
    const info = Data.hireling[profile.merc]?.[0];
    if (!info) return false;
    if (info.class === 560 && base['2handed']) return false;
  }
  return true;
}

function compatibleOffhand(charClass, base1, base2) {
  if (!base1 || !base2) return false;
  const w1 = isSubType(base1.type, 'weap');
  const w2 = isSubType(base2.type, 'weap');
  if (!w1 && !w2) return false; // one needs to be a weapon
  const s1 = isSubType(base1.type, 'shld');
  const s2 = isSubType(base2.type, 'shld');
  const w1h1 = w1 && (!base1['2handed'] || (charClass === 'bar' && base1['1or2handed']));
  const w2h1 = w2 && (!base2['2handed'] || (charClass === 'bar' && base2['1or2handed']));
  if ((w1h1 && s2) || (w2h1 && s1)) return true; // 1h + shield
  if ((charClass === 'bar' || Data.hireling[charClass]?.[0].class === 560) && w1h1 && w2h1) return true; // 1h + 1h
  if (charClass === 'ass' && w1 && w2 && isSubType(base1.type, 'h2h') && isSubType(base2.type, 'h2h')) return true;
  const t1 = Data.itemTypes[base1.type];
  if (w1 && t1.shoots && isSubType(base2.type, t1.shoots)) return true; // bow + quiver
  const t2 = Data.itemTypes[base2.type];
  if (w2 && t2.shoots && isSubType(base1.type, t2.shoots)) return true; // bow + quiver
  return false;
}

function isAmmoFor(state, id1, id2) {
  const item1 = state.items[id1] || presetItems[id1];
  const item2 = state.items[id2] || presetItems[id2];
  if (!item1 || !item2) return false;
  const base1 = Data.items[item1.base], base2 = Data.items[item2.base];
  if (!base1 || !base2) return false;
  const t1 = Data.itemTypes[base1.type];
  return t1.shoots && isSubType(base2.type, t1.shoots);
}

export function profileEquipItem(profile, slot, id, state) {
  if (!id) { // ez, just unequip
    if (!profile.items[slot]) return profile;
    profile = {...profile};
    profile.items = {...profile.items};
    const oh = otherHandSlot[slot];
    if (oh && isAmmoFor(state, profile.items[slot], profile.items[oh])) {
      delete profile.items[oh];
    }
    delete profile.items[slot];
    return profile;
  }

  if (profile.items[slot] === id) return profile;
  profile = {...profile};
  profile.items = {...profile.items};
  profile.items[slot] = id;
  const oh = otherHandSlot[slot];
  if (oh && profile.items[oh]) {
    const oid = profile.items[oh];
    const i1 = state.items[id] || presetItems[id];
    const i2 = state.items[oid] || presetItems[oid];
    if (!compatibleOffhand(profile.class, Data.items[i1?.base], Data.items[i2?.base])) {
      delete profile.items[oh];
    }
  }
  if (oh && !profile.items[oh]) {
    if (isAmmoFor(state, id, 'aqv')) {
      profile.items[oh] = 'aqv';
    } else if (isAmmoFor(state, id, 'cqv')) {
      profile.items[oh] = 'cqv';
    }
  }
  return profile;
}

export function profileEquipMerc(profile, slot, id, state) {
  if (!id) { // ez, just unequip
    if (!profile.mercItems[slot]) return profile;
    profile = {...profile};
    profile.mercItems = {...profile.mercItems};
    delete profile.mercItems[slot];
    return profile;
  }

  if (profile.mercItems[slot] === id) return profile;
  profile = {...profile};
  profile.mercItems = {...profile.mercItems};
  profile.mercItems[slot] = id;
  const oh = otherHandSlot[slot];
  if (oh && profile.mercItems[oh]) {
    const oid = profile.mercItems[oh];
    const i1 = state.items[id] || presetItems[id];
    const i2 = state.items[oid] || presetItems[oid];
    if (!compatibleOffhand(profile.merc, Data.items[i1?.base], Data.items[i2?.base])) {
      delete profile.mercItems[oh];
    }
  }
  return profile;
}

export function profileSetClass(profile, charClass, state) {
  if (profile.class === charClass) return profile;
  profile = {...profile, class: charClass};
  const items = {};
  for (const slot in profile.items) {
    const id = profile.items[slot];
    const item = state.items[id] || presetItems[id];
    if (item && profileCanEquip(profile, slot, item)) {
      const oh = otherHandSlot[slot];
      if (oh && items[oh]) {
        const oid = items[oh];
        const i2 = state.items[oid] || presetItems[oid];
        if (compatibleOffhand(profile.class, Data.items[item?.base], Data.items[i2?.base])) {
          items[slot] = id;
        }
      } else {
        items[slot] = id;
      }
    }
  }
  profile.items = items;
  profile.skills = {};
  profile.skillProgression = null;
  profile.skillPos = 0;
  return profile;
}

export function profileSetMerc(profile, merc, state) {
  if (profile.merc === merc) return profile;
  profile = {...profile, merc};
  if (merc) {
    const items = {};
    for (const slot in profile.mercItems) {
      const id = profile.mercItems[slot];
      const item = state.items[id] || presetItems[id];
      if (item && profileCanEquip(profile, slot, item, merc)) {
        const oh = otherHandSlot[slot];
        if (oh && items[oh]) {
          const oid = items[oh];
          const i2 = state.items[oid] || presetItems[oid];
          if (compatibleOffhand(merc, Data.items[item?.base], Data.items[i2?.base])) {
            items[slot] = id;
          }
        } else {
          items[slot] = id;
        }
      }
    }
    profile.mercItems = items;
    //profile.mercLevel = Math.max(profile.mercLevel, Data.hireling[merc]?.[0].level || 1);
  }
  return profile;
}

// this will MUTATE profile and return state with new items
export function stateMergeItems(state, items, ...profiles) {
  state = {...state};
  state.items = {...state.items};

  const mapping = new Map();
  const dedupe = new Map();
  for (const id in state.items) {
    const item = state.items[id];
    if (!dedupe.has(item.base)) dedupe.set(item.base, []);
    dedupe.get(item.base).push(id);
  }

  let destId = 1;
  for (const id in items) {
    const item = items[id] = itemFix(items[id]);
    if (!item) {
      mapping.set(id, '0');
      continue;
    }

    if (!dedupe.has(item.base)) dedupe.set(item.base, []);
    let newId = dedupe.get(item.base).find(id => itemsEqual(item, state.items[id]));
    if (!newId) {
      while (state.items[destId]) {
        destId += 1;
      }
      newId = destId++;
      state.items[newId] = item;
      dedupe.get(item.base).push(newId);
    }
    mapping.set(id, newId.toString());
  }

  for (const item of Object.values(items)) {
    if (item?.socketedItems) {
      item.socketedItems = item.socketedItems.map(id => (id && mapping.get(id.toString())) || id);
    }
  }

  for (const profile of profiles) {
    if (profile.items) for (const id in profile.items) {
      profile.items[id] = (profile.items[id] && mapping.get(profile.items[id].toString())) || profile.items[id];
    }
    if (profile.mercItems) for (const id in profile.mercItems) {
      profile.mercItems[id] = (profile.mercItems[id] && mapping.get(profile.mercItems[id].toString())) || profile.mercItems[id];
    }
    if (profile.inventory) profile.inventory = profile.inventory.map(id => (id && mapping.get(id.toString())) || id);
    if (profile.cube) profile.cube = profile.cube.map(id => (id && mapping.get(id.toString())) || id);
    if (profile.ironGolem) profile.ironGolem = mapping.get(profile.ironGolem.toString()) || profile.ironGolem;
  }

  return state;
}

export function stateAddProfile(state, profile, items, buffs, summons) {
  const active = state.profiles[state.activeProfile];
  let index = -1;
  if (state.imported || active.imported || profileIsEmpty(active)) {
    index = state.activeProfile;
    const only = state.profiles.length === 1;
    state = stateRemoveProfile(state, active.id);
    if (only) state.profiles.pop();
  }

  state = stateMergeItems(state, items, profile);

  profile = profileFix(profile, state.items);
  if (state.profiles.find(p => p.name === profile.name)) {
    profile.name = `Set ${state.profiles.length + 1}`;
  }
  state.profiles = state.profiles.slice();
  if (index < 0) {
    state.activeProfile = state.profiles.length;
    state.profiles.push(profile);
  } else {
    state.activeProfile = index;
    state.profiles.splice(index, 0, profile);
  }

  if (buffs) state.buffs = buffs;
  if (summons) state.summons = summons;

  return state;
}

export function profileMaxAttributes(profile) {
  return (profile.level - 1) * 5 + (profile.quests?.reduce((sum, q) => sum + (q.lamessenstome ? 5 : 0), 0) || 0);
}

export function profileUpdateAttributes(profile) {
  let total = Object.values(profile.stats).reduce((sum, value) => sum + value, 0);
  const max = profileMaxAttributes(profile);
  if (total > max) {
    profile = {...profile};
    const stats = profile.stats = {...profile.stats};
    while (total > max) {
      const entries = Object.entries(stats).sort(([, v1], [, v2]) => v2 - v1);
      const reduce = Math.min(Math.max(1, entries[0][1] - (entries[1]?.[1] || 0)), total - max);
      stats[entries[0][0]] -= reduce;
      total -= reduce;
    }
  }
  return profile;
}

export function profileIsEmpty(profile) {
  if (Object.values(profile.skills).some(Boolean)) return false;
  if (profile.skillProgression?.length) return false;
  if (profile.inventory.some(Boolean)) return false;
  if (profile.cube.some(Boolean)) return false;
  if (profile.merc) return false;
  if (Object.values(profile.items).some(Boolean)) return false;
  return true;
}

export function stateIsEmpty(state) {
  return state.profiles.length === 1 && profileIsEmpty(state.profiles[0]) && !Object.values(state.items).some(Boolean);
}

export function profileIsMercenary(profile) {
  if (!profile.merc) return false;
  if (!Object.values(profile.mercItems).some(Boolean)) return false;
  if (profile.skillProgression?.length) return false;
  if (Object.values(profile.items).some(Boolean)) return false;
  if (Object.values(profile.skills).some(Boolean)) return false;
  if (profile.inventory.some(Boolean)) return false;
  if (profile.cube.some(Boolean)) return false;
  return true;
}

export function profileSetDifficulty(profile, difficulty) {
  if (profile.difficulty === difficulty) return profile;
  profile = {...profile, difficulty};
  if (profile.monster) {
    profile.monster = monsterFix(profile.monster, difficulty, profile.level);
  }
  return profile;
}

export function* profileListItems(profile) {
  if (profile.items) for (const id of Object.values(profile.items)) {
    if (id && !presetItems[id]) yield id;
  }
  if (profile.mercItems) for (const id of Object.values(profile.mercItems)) {
    if (id && !presetItems[id]) yield id;
  }
  if (profile.inventory) for (const id of profile.inventory) {
    if (id && !presetItems[id]) yield id;
  }
  if (profile.cube) for (const id of profile.cube) {
    if (id && !presetItems[id]) yield id;
  }
  if (profile.ironGolem && !presetItems[profile.ironGolem]) yield profile.ironGolem;
}

export function stateIsSingleProfile(state) {
  if (state.profiles.length !== 1) return false;
  const items = new Set(Object.keys(state.items).filter(id => state.items[id]));
  for (const id of profileListItems(state.profiles[0])) {
    items.delete(id);
    const item = state.items[id];
    if (item?.socketedItems) {
      for (const id of item.socketedItems) {
        items.delete(id);
      }
    }
  }
  return !items.size;
}

export function stateRemoveProfile(state, ...ids) {
  const toRemove = state.profiles.filter(profile => ids.includes(profile.id));
  if (!toRemove.length) return state;
  state = {...state};
  const active = state.profiles[state.activeProfile];
  state.profiles = state.profiles.filter(profile => !ids.includes(profile.id));
  const used = new Set();
  for (const profile of state.profiles) {
    for (const id of profileListItems(profile)) {
      used.add(id);
    }
  }
  state.items = {...state.items};
  const deleted = new Set();
  for (const profile of toRemove) {
    for (const id of profileListItems(profile)) {
      if (!used.has(id)) {
        deleted.add(id);
      }
    }
  }
  for (const id in state.items) {
    const sock = state.items[id]?.socketedItems;
    if (sock && !deleted.has(id)) {
      for (const sid of sock) {
        used.add(sid);
      }
    }
  }
  for (const id of deleted) {
    if (!used.has(id)) {
      const item = state.items[id];
      if (item?.socketedItems) {
        for (const sid of item.socketedItems) {
          if (!used.has(sid)) {
            delete state.items[sid];
          }
        }
      }
      delete state.items[id];
    }
  }
  if (!state.profiles.length) {
    state.profiles.push({...ProfileStore.initialState, class: active.class, gender: active.gender, seasonal: active.seasonal, id: Store.newId()});
  }
  state.activeProfile = state.profiles.findIndex(profile => profile.id === active.id);
  if (state.activeProfile < 0) state.activeProfile = 0;
  return state;
}

export function stateSetImported(state, id, userId, profileId, name) {
  if (profileId) {
    state = {...state};
    delete state.imported;
    delete state.importedId;
    delete state.user;
    state.profiles = state.profiles.map(profile => {
      if (profile.id === profileId) {
        profile = {...profile, imported: true, importedId: id, user: userId};
        if (name) profile.name = name;
      } else if (profile.importedId === id) {
        profile = {...profile};
        delete profile.imported;
        delete profile.importedId;
        delete profile.user;
      }
      return profile;
    });
  } else {
    state = {...state, imported: true, importedId: id, user: userId};
    state.profiles = state.profiles.map(profile => {
      if (profile.importedId) {
        profile = {...profile};
        delete profile.imported;
        delete profile.importedId;
        delete profile.user;
      }
      return profile;
    });
    if (name) state.name = name;
  }
  return state;
}

export function profileSelectSkills(profile) {
  if (!profile.skillProgression || !profile.skillPos) {
    return profile.skills;
  }
  const skills = {...profile.skills};
  for (let i = 0; i < profile.skillPos; ++i) {
    const id = profile.skillProgression[i];
    if (id) skills[id] = (skills[id] || 0) + 1;
  }
  return skills;
}

export function stateDeleteImports(state, ...ids) {
  if (ids.includes(state.importedId) || state.profiles.some(p => ids.includes(p.importedId))) {
    state = {...state};
    if (ids.includes(state.importedId)) {
      delete state.imported;
      delete state.importedId;
      delete state.user;
    }
    state.profiles = state.profiles.map(profile => {
      if (ids.includes(profile.importedId)) {
        profile = {...profile};
        delete profile.imported;
        delete profile.importedId;
        delete profile.user;
      }
      return profile;
    });
  }
  return state;
}
